import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { InputField } from "../../components/ui/InputField";
import Button from "../../components/ui/Button";
import AuthLayout from "../../components/layouts/AuthLayout";
import { AuthAPI } from "../../services/api";

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [resetToken, setResetToken] = useState("");

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError("");

    try {
      const data = await AuthAPI.forgotPassword(email);
      if (data.success) {
        // Store the reset token from the response
        if (data.reset_token) {
          setResetToken(data.reset_token);
        }
        setStep(2); // Move to "check email" step
      } else {
        setError(data.message || "Failed to send reset email");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSignIn = () => {
    navigate("/auth/signin");
  };

  const handleTryAgain = () => {
    setStep(1);
    setError("");
  };

  const handleSimulateResetLink = () => {
    // Simulate clicking reset link - navigate to reset password page with token
    const tokenToUse = resetToken || "fallback-reset-token-123";
    navigate(`/auth/reset-password?token=${tokenToUse}&email=${email}`);
  };

  const renderStep1 = () => (
    <>
      <form
        onSubmit={handleEmailSubmit}
        className="flex relative flex-col gap-1.5 items-start z-[2] w-[80%]"
      >
        <div className="text-sm text-[#403634]">
          Please confirm your registered email in the field below to receive a
          reset password link.
        </div>
        <InputField
          label="Your Email"
          placeholder="Email@example.com"
          type="email"
          value={email}
          onChange={setEmail}
        />
      </form>

      {error && (
        <div className="relative self-stretch text-sm text-red-600 text-center z-[2]">
          {error}
        </div>
      )}

      <Button
        onClick={handleEmailSubmit}
        disabled={loading || !email}
        size="sm"
        variant="primary"
        className="z-[2]"
        loading={loading}
      >
        Reset password
      </Button>

      <div className="flex relative gap-2.5 justify-center items-start self-stretch z-[2]">
        <span className="text-sm text-stone-500 max-sm:text-xs">
          Remember your password?
        </span>
        <button
          type="button"
          onClick={handleBackToSignIn}
          className="text-sm font-semibold cursor-pointer text-stone-900 max-sm:text-xs hover:text-stone-700 transition-colors"
        >
          Back to Sign In
        </button>
      </div>
    </>
  );

  const renderStep2 = () => (
    <>
      <div className="relative text-center z-[2] space-y-4">
        <div className="text-lg font-semibold text-[#403634]">
          PLEASE CHECK YOUR EMAIL!
        </div>
        <div className="text-sm text-[#403634]">
          A password reset link has been sent to your mail box, please check and
          click into the link inside to reset your password.
        </div>
      </div>

      <div className="flex relative gap-2.5 justify-center items-start self-stretch z-[2]">
        <span className="text-sm text-stone-500 max-sm:text-xs">
          Didn't receive an email?
        </span>
        <button
          type="button"
          onClick={handleTryAgain}
          className="text-sm font-semibold cursor-pointer text-stone-900 max-sm:text-xs hover:text-stone-700 transition-colors"
        >
          Try again
        </button>
      </div>
    </>
  );

  const getTitle = () => {
    switch (step) {
      case 1:
        return "";
      case 2:
        return "";
      default:
        return "RESET PASSWORD";
    }
  };

  const renderContent = () => {
    switch (step) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      default:
        return renderStep1();
    }
  };

  return <AuthLayout title={getTitle()}>{renderContent()}</AuthLayout>;
}

export default ForgotPasswordPage;
